// Settings and draft, saved in localStorage when it works (spec §7.1).
import { builtinProfileSet, resolveConfig, Checker, type Config, type Profile } from '../engine';

export const SETTINGS_KEY = 'serpsize:settings:v1';
export const DRAFT_KEY = 'serpsize:draft:v1';

export type Theme = 'system' | 'light' | 'dark';

export interface Settings {
  /** Enabled profile ids; null means the profiles marked default. */
  profiles: string[] | null;
  thresholds: Partial<Pick<Config, 'titleLimitPx' | 'descCharCap' | 'tightTitlePx' | 'tightDescPx' | 'lowShownPct' | 'overflowTolerancePct'>>;
  brand: string;
  showHidden: boolean;
  theme: Theme;
}

export interface Draft { title: string; description: string; url: string; brand: string; keyword: string; query: string }

export const DEFAULT_SETTINGS: Settings = { profiles: null, thresholds: {}, brand: '', showHidden: true, theme: 'system' };
export const SAMPLE_DRAFT: Draft = {
  url: 'https://example.com/',
  title: 'Project Management Software for Teams to Plan, Track and Ship Work Faster | Example',
  description: "Organize every project with Example's project management platform. Combine roadmaps, sprint planning, shared documentation and reporting to automate busywork and keep distributed teams aligned.",
  brand: 'Example', keyword: '', query: '',
};
// The earlier default draft; a saved copy of it is swapped for the current one.
const OLD_SAMPLE_TITLE = 'Spend Management, Corporate Cards & Accounts Payable Solutions | Ramp';

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return { ...fallback, ...JSON.parse(raw) };
  } catch { return fallback; }
}
function loadDraft(): Draft {
  const d = read<Draft>(DRAFT_KEY, SAMPLE_DRAFT);
  return d.title === OLD_SAMPLE_TITLE && d.brand === 'Ramp' ? SAMPLE_DRAFT : d;
}
function write(key: string, value: unknown) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable */ }
}

export const profileSet = builtinProfileSet();

type Listener = () => void;
const listeners = new Set<Listener>();

export const state = {
  settings: read<Settings>(SETTINGS_KEY, DEFAULT_SETTINGS),
  draft: loadDraft(),
  config: resolveConfig(),
  checker: null as unknown as Checker,
};

function rebuild() {
  state.config = resolveConfig(state.settings.thresholds);
  state.checker = new Checker(profileSet, state.config);
}
rebuild();

export function enabledProfiles(): Profile[] {
  const ids = state.settings.profiles;
  const ps = profileSet.list.filter(p => (ids ? ids.includes(p.id) : p.default));
  return ps.length ? ps : profileSet.list.filter(p => p.default);
}

export function updateSettings(patch: Partial<Settings>) {
  state.settings = { ...state.settings, ...patch };
  write(SETTINGS_KEY, state.settings);
  rebuild();
  listeners.forEach(l => l());
}

export function resetSettings() {
  state.settings = { ...DEFAULT_SETTINGS, theme: state.settings.theme };
  write(SETTINGS_KEY, state.settings);
  rebuild();
  listeners.forEach(l => l());
}

export function saveDraft(patch: Partial<Draft>) {
  state.draft = { ...state.draft, ...patch };
  write(DRAFT_KEY, state.draft);
}

export function onSettings(l: Listener) { listeners.add(l); return () => listeners.delete(l); }

/** Dark mode is the `dark` class on <html>: chosen, or following the system when the theme is 'system'. */
export function applyTheme(t: Theme) {
  const dark = t === 'dark' || (t === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.classList.toggle('dark', dark);
}

export const CLASS_ORDER = ['mobile', 'tablet', 'desktop'] as const;
export const CLASS_LABEL = { mobile: 'Mobile', tablet: 'Tablet', desktop: 'Desktop' } as const;
